import { useEffect, useMemo, useState, useRef } from "react";
import { useRpc } from "../store";
import { useI18n } from "../i18n";
import { useColors, useStyles } from "../ui";
import { EChart, type EChartHandle } from "./EChart";
import { specToOption } from "../insightsChart";
import {
  CHART_TYPES,
  DIMENSIONS,
  BREAKDOWN_DIMENSIONS,
  METRICS,
  encodeSpec,
} from "../chartSpec";
import type {
  ChartSpec,
  ChartResult,
  ChartType,
  ChartDimension,
  ChartMetric,
} from "../../../shared/src/protocol";

interface RpcOpts {
  hospitalIds?: string[];
  unitIds?: string[];
  startTime?: number;
  endTime?: number;
  granularity: "day" | "week" | "month";
}

export function ChartBuilder({
  spec,
  onSpecChange,
  filters,
  refreshKey,
  onRemove,
  onDuplicate,
  chartRef,
}: {
  spec: ChartSpec;
  onSpecChange: (next: ChartSpec) => void;
  filters: RpcOpts;
  refreshKey: number;
  onRemove?: () => void;
  onDuplicate?: () => void;
  chartRef?: React.RefObject<EChartHandle | null>;
}) {
  const rpc = useRpc();
  const { t } = useI18n();
  const colors = useColors();
  const styles = useStyles();
  const internalChartRef = useRef<EChartHandle>(null);
  const activeChartRef = chartRef ?? internalChartRef;

  const [result, setResult] = useState<ChartResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtersKey = JSON.stringify(filters);

  // Debounce so rapid dropdown changes collapse into one roundtrip.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const t = setTimeout(() => {
      rpc<ChartResult>({ type: "chart", spec, ...filters })
        .then((r) => {
          if (!cancelled) setResult(r);
        })
        .catch((e) => {
          if (!cancelled) setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [encodeSpec(spec), filtersKey, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const metricLabel = t(`insights.metric_${spec.metric}`);
  const dimensionLabel = t(`chartBuilder.dim_${spec.dimension}`);
  const option = useMemo(() => {
    if (!result) return null;
    return specToOption(spec, result, colors, metricLabel, dimensionLabel);
  }, [result, spec, colors, metricLabel, dimensionLabel]);

  const update = (patch: Partial<ChartSpec>) => {
    const next = { ...spec, ...patch };
    if (next.chartType === "heatmap" && !next.breakdown) {
      next.breakdown = next.dimension === "time" ? "triage" : "time";
    }
    if (next.chartType === "pie") next.breakdown = undefined;
    if (next.breakdown && next.breakdown === (next.dimension as string)) {
      next.breakdown = undefined;
    }
    onSpecChange(next);
  };

  const breakdownDisabled = spec.chartType === "pie";
  const breakdownRequired = spec.chartType === "heatmap";

  return (
    <div>
      {/* Pickers */}
      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          flexWrap: "wrap",
          alignItems: "center",
          paddingBottom: "0.5rem",
          borderBottom: `1px solid ${colors.border}`,
          marginBottom: "0.5rem",
        }}
      >
        <Field label={t("chartBuilder.type")}>
          <select
            value={spec.chartType}
            onChange={(e) =>
              update({ chartType: e.target.value as ChartType })
            }
            style={{ ...styles.selectCompact, width: "auto" }}
          >
            {CHART_TYPES.map((ct) => (
              <option key={ct} value={ct}>
                {t(`chartBuilder.type_${ct}`)}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t("chartBuilder.dimension")}>
          <select
            value={spec.dimension}
            onChange={(e) =>
              update({ dimension: e.target.value as ChartDimension })
            }
            style={{ ...styles.selectCompact, width: "auto" }}
          >
            {DIMENSIONS.map((d) => (
              <option key={d} value={d}>
                {t(`chartBuilder.dim_${d}`)}
              </option>
            ))}
          </select>
        </Field>

        <Field label={t("chartBuilder.breakdown")}>
          <select
            value={spec.breakdown ?? ""}
            disabled={breakdownDisabled}
            onChange={(e) =>
              update({
                breakdown: e.target.value
                  ? (e.target.value as Exclude<ChartDimension, "trauma">)
                  : undefined,
              })
            }
            style={{
              ...styles.selectCompact,
              width: "auto",
              opacity: breakdownDisabled ? 0.5 : 1,
            }}
          >
            <option value="">
              {breakdownRequired
                ? t("chartBuilder.required")
                : t("chartBuilder.none")}
            </option>
            {BREAKDOWN_DIMENSIONS.filter((d) => d !== spec.dimension).map(
              (d) => (
                <option key={d} value={d}>
                  {t(`chartBuilder.dim_${d}`)}
                </option>
              ),
            )}
          </select>
        </Field>

        <Field label={t("chartBuilder.metric")}>
          <select
            value={spec.metric}
            onChange={(e) => update({ metric: e.target.value as ChartMetric })}
            style={{ ...styles.selectCompact, width: "auto" }}
          >
            {METRICS.map((m) => (
              <option key={m} value={m}>
                {t(`insights.metric_${m}`)}
              </option>
            ))}
          </select>
        </Field>

        {(onDuplicate || onRemove) && (
          <div style={{ marginLeft: "auto", display: "flex", gap: "0.25rem" }}>
            {onDuplicate && (
              <button
                onClick={onDuplicate}
                title={t("chartBuilder.duplicate")}
                style={{
                  ...styles.btnSmall,
                  background: colors.bg,
                  color: colors.text,
                  borderColor: colors.border,
                }}
              >
                {t("chartBuilder.duplicate")}
              </button>
            )}
            {onRemove && (
              <button
                onClick={onRemove}
                title={t("chartBuilder.remove")}
                style={{
                  ...styles.btnSmall,
                  background: colors.bg,
                  color: colors.danger,
                  borderColor: colors.border,
                }}
              >
                ×
              </button>
            )}
          </div>
        )}
      </div>

      {/* Chart preview */}
      <div style={{ height: "20rem", position: "relative" }}>
        {loading && !result && (
          <div
            style={{
              padding: "1rem",
              color: colors.textMuted,
              textAlign: "center",
            }}
          >
            {t("app.loading")}
          </div>
        )}
        {error && (
          <div
            style={{
              padding: "1rem",
              color: colors.danger,
              fontSize: "0.8rem",
            }}
          >
            {error}
          </div>
        )}
        {option && result && result.rows.length > 0 && (
          <EChart ref={activeChartRef} option={option} />
        )}
        {result && result.rows.length === 0 && !loading && (
          <div
            style={{
              padding: "1rem",
              color: colors.textMuted,
              textAlign: "center",
            }}
          >
            {t("chartBuilder.noData")}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  const colors = useColors();
  return (
    <div style={{ display: "flex", gap: "0.375rem", alignItems: "center" }}>
      <span
        style={{
          fontSize: "0.75rem",
          fontWeight: 600,
          color: colors.textMuted,
          textTransform: "uppercase",
        }}
      >
        {label}:
      </span>
      {children}
    </div>
  );
}
